/**
 * AdminBillingOps - Admin Billing Operations Panel
 * 
 * Uses ONLY pre-existing billing admin endpoints:
 * - GET /api/billing/admin/tenant-overview/{tenant_id} (NEW: read-only overview)
 * - GET /api/billing/admin/ledger (paginated ledger with tenant_id filter)
 * - GET /api/billing/admin/ledger/{id} (single ledger entry)
 * - POST /api/billing/admin/run-renewals
 * - POST /api/billing/admin/run-retries
 * - POST /api/billing/admin/refund
 * 
 * Structure:
 * 1. Tenant Billing Overview (READ-ONLY diagnostics) - DEFAULT view
 * 2. Advanced / Break Glass (operations that write data) - COLLAPSIBLE
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  ArrowClockwise,
  CreditCard,
  ArrowCounterClockwise,
  Warning,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  CaretLeft,
  CaretRight,
  CaretDown,
  CaretUp,
  User,
  ShieldCheck,
  ShieldWarning,
  Calendar,
  Receipt,
  Eye,
  Copy,
} from 'phosphor-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Copy to clipboard helper
const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`${label} copied to clipboard`);
  }).catch(() => {
    toast.error('Failed to copy');
  });
};

// Ledger type colors
const LEDGER_TYPE_COLORS = {
  CHARGE: 'bg-blue-500/20 text-blue-400',
  REFUND: 'bg-purple-500/20 text-purple-400',
  RETRY_RENEWAL: 'bg-amber-500/20 text-amber-400',
  ENFORCEMENT: 'bg-red-500/20 text-red-400',
};

// Status badge colors
const STATUS_COLORS = {
  approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  onboarding: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  requested: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  none: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
};

export default function AdminBillingOps() {
  // URL search params for deep linking from Tenant Management
  const [searchParams] = useSearchParams();
  
  // Tenant ID input (manual entry)
  const [tenantIdInput, setTenantIdInput] = useState('');
  const [tenantIdError, setTenantIdError] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  
  // Track if we've auto-loaded from URL (one-shot)
  const [autoLoadedFromUrl, setAutoLoadedFromUrl] = useState(false);
  
  // Overview state (NEW)
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  
  // Ledger state
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [selectedLedgerEntry, setSelectedLedgerEntry] = useState(null);
  
  // Action states
  const [runningRenewals, setRunningRenewals] = useState(false);
  const [runningRetries, setRunningRetries] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundEntry, setRefundEntry] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  
  // Last run results
  const [lastRenewalResult, setLastRenewalResult] = useState(null);
  const [lastRetryResult, setLastRetryResult] = useState(null);
  
  // Advanced section collapsed state
  const [advancedOpen, setAdvancedOpen] = useState(false);
  
  // P1: Tenant Access Control state
  const [accessStatus, setAccessStatus] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [enableModalOpen, setEnableModalOpen] = useState(false);
  const [disableReason, setDisableReason] = useState('');
  const [enableReason, setEnableReason] = useState('');
  const [disableConfirm, setDisableConfirm] = useState('');
  const [enableConfirm, setEnableConfirm] = useState('');
  const [processingAccess, setProcessingAccess] = useState(false);

  // Load tenant overview (NEW - read-only)
  const loadOverview = useCallback(async (tenantId) => {
    if (!tenantId) {
      toast.error('Please enter a tenant ID');
      return;
    }
    
    setOverviewLoading(true);
    setSelectedTenantId(tenantId);
    setOverview(null);
    
    try {
      // Get auth token for Bearer authentication
      const token = localStorage.getItem('auth_token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(
        `${API}/api/billing/admin/tenant-overview/${encodeURIComponent(tenantId)}`,
        { credentials: 'include', headers }
      );
      
      if (!response.ok) {
        if (response.status === 403) {
          toast.error('Admin access required');
        } else if (response.status === 404) {
          toast.error('Tenant not found');
        } else {
          toast.error('Failed to load overview');
        }
        return;
      }
      
      const data = await response.json();
      setOverview(data);
      setLedger(data.recent_ledger || []);
      setLedgerTotal(data.total_ledger_count || 0);
      setLedgerPage(1);
      
    } catch (error) {
      console.error('Overview load error:', error);
      toast.error('Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  // Auto-load from URL query param (one-shot on mount or param change)
  useEffect(() => {
    const tenantIdParam = searchParams.get('tenant_id');
    
    // Only auto-load if:
    // 1. We have a tenant_id param
    // 2. We haven't already auto-loaded for this param
    // 3. The param looks like a valid tenant_id (starts with tenant_ or is non-empty)
    if (tenantIdParam && !autoLoadedFromUrl) {
      // Basic validation - accept tenant_ prefixed or any non-empty alphanumeric
      const isValidFormat = tenantIdParam.startsWith('tenant_') || /^[a-zA-Z0-9_-]+$/.test(tenantIdParam);
      
      if (isValidFormat) {
        setTenantIdInput(tenantIdParam);
        setAutoLoadedFromUrl(true);
        // Trigger load after state is set
        loadOverview(tenantIdParam);
      }
    }
  }, [searchParams, autoLoadedFromUrl, loadOverview]);

  // Load full ledger for tenant (using pre-existing endpoint)
  const loadLedger = useCallback(async (tenantId, page = 1) => {
    if (!tenantId) return;
    
    setLedgerLoading(true);
    
    try {
      const response = await fetch(
        `${API}/api/billing/admin/ledger?tenant_id=${encodeURIComponent(tenantId)}&page=${page}&page_size=10`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        toast.error('Failed to load ledger');
        return;
      }
      
      const data = await response.json();
      setLedger(data.entries || []);
      setLedgerTotal(data.total_count || 0);
      setLedgerPage(page);
      
    } catch (error) {
      console.error('Ledger load error:', error);
      toast.error('Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  // Load single ledger entry detail
  const loadLedgerDetail = useCallback(async (ledgerId) => {
    if (!selectedTenantId) return;
    
    try {
      const response = await fetch(
        `${API}/api/billing/admin/ledger/${ledgerId}?tenant_id=${encodeURIComponent(selectedTenantId)}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        toast.error('Failed to load ledger entry');
        return;
      }
      
      const data = await response.json();
      setSelectedLedgerEntry(data);
    } catch (error) {
      console.error('Ledger detail error:', error);
      toast.error('Failed to load ledger entry');
    }
  }, [selectedTenantId]);

  // Handle tenant search (load overview)
  const handleSearch = () => {
    const trimmedId = tenantIdInput.trim();
    
    // Validate tenant_id format:
    // - must start with "tenant_"
    // - must be alphanumeric with underscores/hyphens only
    // - length >= 10
    const validTenantIdPattern = /^tenant_[a-zA-Z0-9_-]+$/;
    
    if (!trimmedId) {
      setTenantIdError('');
      return;
    }
    
    if (trimmedId.includes('@')) {
      setTenantIdError('Enter a tenant_id (e.g., tenant_abc123). Emails not supported.');
      return;
    }
    
    if (trimmedId.includes("'") || trimmedId.includes('"') || trimmedId.includes(' ')) {
      setTenantIdError('Enter a tenant_id (e.g., tenant_abc123). Names not supported.');
      return;
    }
    
    if (!validTenantIdPattern.test(trimmedId)) {
      setTenantIdError('Enter a tenant_id (e.g., tenant_abc123). Must start with "tenant_".');
      return;
    }
    
    if (trimmedId.length < 10) {
      setTenantIdError('Enter a tenant_id (e.g., tenant_abc123). ID too short.');
      return;
    }
    
    // Valid - clear error and load
    setTenantIdError('');
    loadOverview(trimmedId);
  };

  // P1: Load tenant access status
  const loadAccessStatus = useCallback(async (tenantId) => {
    if (!tenantId) return;
    
    setAccessLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/api/admin/tenants/${encodeURIComponent(tenantId)}/access-status`,
        { credentials: 'include', headers }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccessStatus(data);
      }
    } catch (error) {
      console.error('Access status load error:', error);
    } finally {
      setAccessLoading(false);
    }
  }, []);

  // Load access status when overview loads
  useEffect(() => {
    if (selectedTenantId && overview) {
      loadAccessStatus(selectedTenantId);
    }
  }, [selectedTenantId, overview, loadAccessStatus]);

  // P1: Disable tenant access
  const handleDisableTenant = async () => {
    if (disableConfirm !== 'DISABLE_TENANT') {
      toast.error('Type DISABLE_TENANT to confirm');
      return;
    }
    if (!disableReason || disableReason.trim().length < 5) {
      toast.error('Reason is required (min 5 characters)');
      return;
    }
    
    setProcessingAccess(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      
      const response = await fetch(`${API}/api/admin/tenants/disable-access`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          tenant_id: selectedTenantId,
          reason: disableReason.trim(),
          confirm: 'DISABLE_TENANT',
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to disable tenant');
      }
      
      const data = await response.json();
      setAccessStatus(data);
      setDisableModalOpen(false);
      setDisableReason('');
      setDisableConfirm('');
      
      if (data.action === 'disabled') {
        toast.success(`Tenant ${selectedTenantId} access disabled`);
      } else {
        toast.info('Tenant was already disabled');
      }
    } catch (error) {
      console.error('Disable error:', error);
      toast.error(error.message || 'Failed to disable tenant');
    } finally {
      setProcessingAccess(false);
    }
  };

  // P1: Enable tenant access
  const handleEnableTenant = async () => {
    if (enableConfirm !== 'ENABLE_TENANT') {
      toast.error('Type ENABLE_TENANT to confirm');
      return;
    }
    
    setProcessingAccess(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      
      const response = await fetch(`${API}/api/admin/tenants/enable-access`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          tenant_id: selectedTenantId,
          reason: enableReason.trim() || null,
          confirm: 'ENABLE_TENANT',
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to enable tenant');
      }
      
      const data = await response.json();
      setAccessStatus(data);
      setEnableModalOpen(false);
      setEnableReason('');
      setEnableConfirm('');
      
      if (data.action === 'enabled') {
        toast.success(`Tenant ${selectedTenantId} access enabled`);
      } else {
        toast.info('Tenant was already enabled');
      }
    } catch (error) {
      console.error('Enable error:', error);
      toast.error(error.message || 'Failed to enable tenant');
    } finally {
      setProcessingAccess(false);
    }
  };

  // Run renewals (using pre-existing endpoint)
  const handleRunRenewals = async (dryRun = false) => {
    setRunningRenewals(true);
    try {
      const response = await fetch(`${API}/api/billing/admin/run-renewals?dry_run=${dryRun}`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to run renewals');
      }
      
      const data = await response.json();
      setLastRenewalResult(data);
      
      if (dryRun) {
        toast.info(`Dry run: ${data.tenants_processed} tenants would be processed`);
      } else {
        toast.success(`Renewals complete: ${data.tenants_succeeded} succeeded, ${data.tenants_failed} failed`);
      }
    } catch (error) {
      console.error('Renewal error:', error);
      toast.error(error.message || 'Failed to run renewals');
    } finally {
      setRunningRenewals(false);
    }
  };

  // Run retries (using pre-existing endpoint)
  const handleRunRetries = async (dryRun = false) => {
    setRunningRetries(true);
    try {
      const response = await fetch(`${API}/api/billing/admin/run-retries?dry_run=${dryRun}`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to run retries');
      }
      
      const data = await response.json();
      setLastRetryResult(data);
      
      if (dryRun) {
        toast.info(`Dry run: ${data.tenants_processed} past-due tenants found`);
      } else {
        toast.success(`Retries complete: ${data.retries_succeeded} succeeded, ${data.retries_failed} failed, ${data.enforcements} enforced`);
      }
    } catch (error) {
      console.error('Retry error:', error);
      toast.error(error.message || 'Failed to run retries');
    } finally {
      setRunningRetries(false);
    }
  };

  // Process refund
  const handleRefund = async () => {
    if (!refundEntry || !selectedTenantId) return;
    
    setProcessingRefund(true);
    try {
      const amountCents = refundAmount ? Math.round(parseFloat(refundAmount) * 100) : null;
      
      const response = await fetch(`${API}/api/billing/admin/refund`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: selectedTenantId,
          ledger_id: refundEntry.ledger_id,
          amount_cents: amountCents,
          reason: 'Admin refund via Billing Ops',
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Refund failed');
      }
      
      const data = await response.json();
      toast.success(`Refund processed: $${(data.refund_amount_cents / 100).toFixed(2)}`);
      
      setRefundModalOpen(false);
      setRefundEntry(null);
      setRefundAmount('');
      
      // Reload overview
      loadOverview(selectedTenantId);
    } catch (error) {
      console.error('Refund error:', error);
      toast.error(error.message || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };

  // Open refund modal
  const openRefundModal = (entry) => {
    setRefundEntry(entry);
    setRefundAmount((entry.amount_cents / 100).toFixed(2));
    setRefundModalOpen(true);
  };

  // Format timestamp
  const formatTs = (ts) => {
    if (!ts) return '-';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  // Format date only
  const formatDate = (ts) => {
    if (!ts) return 'Unknown';
    try {
      return new Date(ts).toLocaleDateString();
    } catch {
      return ts;
    }
  };

  // Format currency
  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* ================================================================ */}
      {/* TENANT BILLING OVERVIEW - READ-ONLY DIAGNOSTICS (DEFAULT VIEW) */}
      {/* ================================================================ */}
      <Card className="bg-zinc-900 border-zinc-700">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg text-zinc-100 flex items-center gap-2">
            <Eye size={20} className="text-[#FF6600]" />
            Tenant Billing Overview
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Read-only diagnostics: status, entitlement, ownership, ledger
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tenant ID Input */}
          <div className="space-y-2">
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  placeholder="tenant_abc123"
                  value={tenantIdInput}
                  onChange={(e) => {
                    setTenantIdInput(e.target.value);
                    setTenantIdError(''); // Clear error on input change
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className={`bg-zinc-800 border-zinc-600 text-zinc-100 ${tenantIdError ? 'border-red-500' : ''}`}
                  data-testid="tenant-id-input"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={overviewLoading || !tenantIdInput.trim()}
                className="bg-[#FF6600] hover:bg-[#E55C00]"
                data-testid="load-overview-btn"
              >
                {overviewLoading ? (
                <ArrowClockwise size={16} className="mr-2 animate-spin" />
              ) : (
                <MagnifyingGlass size={16} className="mr-2" />
              )}
              Load
            </Button>
            </div>
            {/* Validation Error Message */}
            {tenantIdError && (
              <div className="text-red-400 text-sm flex items-center gap-2" data-testid="tenant-id-error">
                <Warning size={14} />
                {tenantIdError}
              </div>
            )}
          </div>
          
          {/* Loading State */}
          {overviewLoading && (
            <div className="space-y-3">
              <Skeleton className="h-24 bg-zinc-800" />
              <Skeleton className="h-16 bg-zinc-800" />
              <Skeleton className="h-16 bg-zinc-800" />
            </div>
          )}
          
          {/* Overview Data */}
          {overview && !overviewLoading && (
            <div className="space-y-4">
              {/* A) STATUS SUMMARY - Top row badges */}
              <div className="p-4 bg-zinc-800 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User size={18} className="text-zinc-400" />
                    <span className="text-zinc-200 font-medium">{overview.tenant_name}</span>
                    <code className="text-xs text-zinc-500 bg-zinc-900 px-2 py-0.5 rounded">
                      {overview.tenant_id}
                    </code>
                  </div>
                </div>
                
                {/* Status Badges Row */}
                <div className="flex flex-wrap gap-2">
                  <Badge className={`${STATUS_COLORS[overview.payment_processing_status] || STATUS_COLORS.none}`}>
                    Finix: {overview.payment_processing_status || 'none'}
                  </Badge>
                  
                  {/* Entitled/Not Entitled Badge - based on payments_entitled (finix approved + entitlement) */}
                  <Badge className={overview.payments_entitled 
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                    : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }>
                    {overview.payments_entitled ? (
                      <><ShieldCheck size={14} className="mr-1" /> Entitled</>
                    ) : (
                      <><ShieldWarning size={14} className="mr-1" /> Not Entitled</>
                    )}
                  </Badge>
                  
                  {/* Fee Status Badge - distinguishes bypassed vs actually paid */}
                  <Badge className={
                    overview.fee_status === 'bypassed' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                    overview.fee_status === 'paid' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                    overview.fee_status === 'comped' ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' :
                    'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
                  }>
                    Fee: {
                      overview.fee_status === 'bypassed' ? 'Bypassed' :
                      overview.fee_status === 'paid' ? 'Paid' :
                      overview.fee_status === 'comped' ? 'Comped' :
                      'Unpaid'
                    }
                  </Badge>
                </div>
                
                {/* Blocked Reason */}
                {overview.blocked_reason && (
                  <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-900/20 p-2 rounded">
                    <Warning size={16} />
                    <span>Blocked: {overview.blocked_reason}</span>
                  </div>
                )}
                
                {!overview.blocked_reason && overview.payments_entitled && (
                  <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-900/20 p-2 rounded">
                    <CheckCircle size={16} />
                    <span>Payments Enabled</span>
                  </div>
                )}
              </div>
              
              {/* FINIX IDENTIFIERS - Copy-to-clipboard section */}
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-3">
                  <CreditCard size={16} />
                  Finix Identifiers
                </div>
                <div className="space-y-2">
                  {/* Buyer ID - always show row, "—" if missing */}
                  <div className="flex items-center justify-between p-2 bg-zinc-900 rounded">
                    <div>
                      <span className="text-zinc-500 text-xs">Buyer ID:</span>
                      {overview.finix_buyer_id ? (
                        <code className="ml-2 text-zinc-200 text-sm font-mono" data-testid="finix-buyer-id">
                          {overview.finix_buyer_id}
                        </code>
                      ) : (
                        <span className="ml-2 text-zinc-500 text-sm" data-testid="finix-buyer-id-empty">—</span>
                      )}
                    </div>
                    {overview.finix_buyer_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(overview.finix_buyer_id, 'Buyer ID')}
                        className="text-zinc-400 hover:text-zinc-200 h-7 w-7 p-0"
                        data-testid="copy-buyer-id-btn"
                      >
                        <Copy size={14} />
                      </Button>
                    )}
                  </div>
                  {/* Onboarding Form ID - always show row, "—" if missing */}
                  <div className="flex items-center justify-between p-2 bg-zinc-900 rounded">
                    <div>
                      <span className="text-zinc-500 text-xs">Onboarding Form ID:</span>
                      {overview.finix_onboarding_form_id ? (
                        <code className="ml-2 text-zinc-200 text-sm font-mono" data-testid="finix-onboarding-form-id">
                          {overview.finix_onboarding_form_id}
                        </code>
                      ) : (
                        <span className="ml-2 text-zinc-500 text-sm" data-testid="finix-onboarding-form-id-empty">—</span>
                      )}
                    </div>
                    {overview.finix_onboarding_form_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(overview.finix_onboarding_form_id, 'Onboarding Form ID')}
                        className="text-zinc-400 hover:text-zinc-200 h-7 w-7 p-0"
                        data-testid="copy-onboarding-form-id-btn"
                      >
                        <Copy size={14} />
                      </Button>
                    )}
                  </div>
                  {/* Merchant ID - always show row, "—" if missing */}
                  <div className="flex items-center justify-between p-2 bg-zinc-900 rounded">
                    <div>
                      <span className="text-zinc-500 text-xs">Merchant ID:</span>
                      {overview.finix_merchant_id ? (
                        <code className="ml-2 text-zinc-200 text-sm font-mono" data-testid="finix-merchant-id">
                          {overview.finix_merchant_id}
                        </code>
                      ) : (
                        <span className="ml-2 text-zinc-500 text-sm" data-testid="finix-merchant-id-empty">—</span>
                      )}
                    </div>
                    {overview.finix_merchant_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(overview.finix_merchant_id, 'Merchant ID')}
                        className="text-zinc-400 hover:text-zinc-200 h-7 w-7 p-0"
                        data-testid="copy-merchant-id-btn"
                      >
                        <Copy size={14} />
                      </Button>
                    )}
                  </div>
                  {/* Finix Status - always show row */}
                  <div className="flex items-center justify-between p-2 bg-zinc-900 rounded">
                    <div>
                      <span className="text-zinc-500 text-xs">Finix Status:</span>
                      {overview.finix_status ? (
                        <span className="ml-2 text-zinc-200 text-sm" data-testid="finix-status">
                          {overview.finix_status}
                          {overview.finix_merchant_onboarding_state && (
                            <span className="text-zinc-500 ml-1">({overview.finix_merchant_onboarding_state})</span>
                          )}
                        </span>
                      ) : (
                        <span className="ml-2 text-zinc-500 text-sm" data-testid="finix-status-empty">—</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* B) OWNERSHIP / SUBSCRIPTION */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Payment Method */}
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                    <CreditCard size={16} />
                    Payment Method
                  </div>
                  {overview.payment_method ? (
                    <div className="text-zinc-200">
                      <span className="font-medium">{overview.payment_method.brand || 'Card'}</span>
                      {' '}•••• {overview.payment_method.last4}
                      <span className="text-zinc-500 ml-2">
                        Exp: {overview.payment_method.exp_month}/{overview.payment_method.exp_year}
                      </span>
                      <Badge className="ml-2 bg-zinc-700 text-zinc-300 text-xs">
                        {overview.payment_method.status}
                      </Badge>
                    </div>
                  ) : (
                    <div className="text-zinc-500">No payment method on file</div>
                  )}
                </div>
                
                {/* Next Billing */}
                <div className="p-4 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                    <Calendar size={16} />
                    Next Billing Date
                  </div>
                  {/* Use current_period_end from subscription when active, fallback to next_renewal_ts */}
                  {overview.active_subscription && overview.current_period_end ? (
                    <div className="text-zinc-200">{formatDate(overview.current_period_end)}</div>
                  ) : overview.next_renewal_ts ? (
                    <div className="text-zinc-200">{formatDate(overview.next_renewal_ts)}</div>
                  ) : (
                    <div className="text-zinc-500">Unknown (no active subscription)</div>
                  )}
                  {overview.billing_anchor_ts && (
                    <div className="text-xs text-zinc-500 mt-1">
                      Anchor: {formatDate(overview.billing_anchor_ts)}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Entitlements List */}
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-2">
                  <Receipt size={16} />
                  Entitlements ({overview.entitlements?.length || 0})
                </div>
                {overview.entitlements?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {overview.entitlements.map((ent) => (
                      <Badge key={ent.entitlement_id} className="bg-zinc-700 text-zinc-300">
                        {ent.entitlement_type}
                        {ent.source && <span className="text-zinc-500 ml-1">({ent.source})</span>}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-zinc-500 text-sm">No entitlements</div>
                )}
              </div>
              
              {/* E) TENANT ACCESS CONTROL */}
              <div className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-2 text-zinc-400 text-sm mb-3">
                  <Warning size={16} className="text-amber-400" />
                  Tenant Access Control
                </div>
                
                {accessLoading ? (
                  <div className="text-zinc-500 text-sm">Loading access status...</div>
                ) : accessStatus?.access_disabled ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-red-900/30 border border-red-600/30 rounded-lg">
                      <div className="flex items-center gap-2 text-red-400 font-medium">
                        <XCircle size={16} />
                        Access Disabled
                      </div>
                      <div className="text-zinc-400 text-sm mt-2">
                        <div><span className="text-zinc-500">Reason:</span> {accessStatus.access_disabled_reason || 'N/A'}</div>
                        <div><span className="text-zinc-500">Disabled by:</span> {accessStatus.access_disabled_by || 'N/A'}</div>
                        <div><span className="text-zinc-500">Disabled at:</span> {accessStatus.access_disabled_at ? formatDate(accessStatus.access_disabled_at) : 'N/A'}</div>
                      </div>
                    </div>
                    <Button
                      onClick={() => setEnableModalOpen(true)}
                      variant="outline"
                      size="sm"
                      className="bg-emerald-900/30 border-emerald-600/50 text-emerald-400 hover:bg-emerald-900/50"
                      data-testid="enable-tenant-btn"
                    >
                      <CheckCircle size={14} className="mr-2" />
                      Re-enable Tenant Access
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 bg-emerald-900/30 border border-emerald-600/30 rounded-lg">
                      <div className="flex items-center gap-2 text-emerald-400 font-medium">
                        <CheckCircle size={16} />
                        Access Enabled
                      </div>
                      <div className="text-zinc-400 text-sm mt-1">
                        Tenant users can access the application normally.
                      </div>
                    </div>
                    <Button
                      onClick={() => setDisableModalOpen(true)}
                      variant="outline"
                      size="sm"
                      className="bg-red-900/30 border-red-600/50 text-red-400 hover:bg-red-900/50"
                      data-testid="disable-tenant-btn"
                    >
                      <XCircle size={14} className="mr-2" />
                      Disable Tenant Access
                    </Button>
                  </div>
                )}
              </div>
              
              {/* D) RECENT BILLING LEDGER */}
              <div className="p-4 bg-zinc-800 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <Receipt size={16} />
                    Recent Ledger ({overview.total_ledger_count || 0} total)
                  </div>
                  {overview.total_ledger_count > 10 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => loadLedger(selectedTenantId, 1)}
                      className="text-zinc-400 hover:text-zinc-200 text-xs"
                    >
                      View All
                    </Button>
                  )}
                </div>
                
                {ledger.length > 0 ? (
                  <div className="space-y-2">
                    {ledger.slice(0, 5).map((entry) => (
                      <div 
                        key={entry.ledger_id}
                        className="flex items-center justify-between p-2 bg-zinc-900 rounded text-sm"
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={`text-xs ${LEDGER_TYPE_COLORS[entry.type] || 'bg-zinc-700'}`}>
                            {entry.type}
                          </Badge>
                          <span className="text-zinc-400">{formatDate(entry.created_at)}</span>
                          <span className="text-zinc-500 text-xs">{entry.reason || ''}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`font-medium ${entry.type === 'REFUND' ? 'text-purple-400' : 'text-zinc-200'}`}>
                            {entry.type === 'REFUND' ? '-' : ''}{formatCurrency(entry.amount_cents)}
                          </span>
                          {entry.status === 'SUCCEEDED' ? (
                            <CheckCircle size={14} className="text-emerald-400" />
                          ) : entry.status === 'FAILED' ? (
                            <XCircle size={14} className="text-red-400" />
                          ) : (
                            <Clock size={14} className="text-amber-400" />
                          )}
                        </div>
                      </div>
                    ))}
                    {ledger.length > 5 && (
                      <div className="text-center text-zinc-500 text-xs pt-2">
                        +{ledger.length - 5} more entries
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-zinc-500 text-sm">No ledger entries</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ================================================================ */}
      {/* ADVANCED / BREAK GLASS - BILLING OPERATIONS (COLLAPSIBLE) */}
      {/* ================================================================ */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <Card className="bg-zinc-900 border-zinc-700">
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-4 cursor-pointer hover:bg-zinc-800/50 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg text-zinc-100 flex items-center gap-2">
                    <Warning size={20} className="text-amber-400" />
                    Advanced / Break Glass
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Admin tools for managing renewals, retries, and refunds
                  </CardDescription>
                </div>
                {advancedOpen ? (
                  <CaretUp size={20} className="text-zinc-400" />
                ) : (
                  <CaretDown size={20} className="text-zinc-400" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="space-y-4 border-t border-zinc-700 pt-4">
              {/* Warning Banner */}
              <div className="p-3 bg-amber-900/20 border border-amber-600/30 rounded-lg">
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <Warning size={16} />
                  These actions can trigger billing runs and charges. Use only when necessary.
                </div>
              </div>
              
              {/* Global Actions */}
              <div className="flex flex-wrap gap-3">
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleRunRenewals(true)}
                    disabled={runningRenewals}
                    variant="outline"
                    size="sm"
                    className="bg-zinc-800 border-zinc-600 text-zinc-200 hover:bg-zinc-700 hover:text-zinc-100"
                    data-testid="run-renewals-dry-btn"
                  >
                    <ArrowClockwise size={16} className="mr-2" />
                    Renewals (Dry Run)
                  </Button>
                  <Button
                    onClick={() => handleRunRenewals(false)}
                    disabled={runningRenewals}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="run-renewals-btn"
                  >
                    {runningRenewals ? <ArrowClockwise size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
                    Run Renewals
                  </Button>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleRunRetries(true)}
                    disabled={runningRetries}
                    variant="outline"
                    size="sm"
                    className="bg-zinc-800 border-zinc-600 text-zinc-200 hover:bg-zinc-700 hover:text-zinc-100"
                    data-testid="run-retries-dry-btn"
                  >
                    <ArrowCounterClockwise size={16} className="mr-2" />
                    Retries (Dry Run)
                  </Button>
                  <Button
                    onClick={() => handleRunRetries(false)}
                    disabled={runningRetries}
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="run-retries-btn"
                  >
                    {runningRetries ? <ArrowClockwise size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
                    Run Retries
                  </Button>
                </div>
              </div>
              
              {/* Last Results */}
              {lastRenewalResult && (
                <div className="p-3 bg-zinc-800 rounded-lg text-sm">
                  <div className="font-medium text-zinc-200 mb-1">Last Renewal Run</div>
                  <div className="text-zinc-400">
                    Processed: {lastRenewalResult.tenants_processed} | 
                    Succeeded: <span className="text-emerald-400">{lastRenewalResult.tenants_succeeded}</span> | 
                    Failed: <span className="text-red-400">{lastRenewalResult.tenants_failed}</span> | 
                    Charged: {formatCurrency(lastRenewalResult.total_charged_cents || 0)}
                  </div>
                </div>
              )}
              
              {lastRetryResult && (
                <div className="p-3 bg-zinc-800 rounded-lg text-sm">
                  <div className="font-medium text-zinc-200 mb-1">Last Retry Run</div>
                  <div className="text-zinc-400">
                    Processed: {lastRetryResult.tenants_processed} | 
                    Retries OK: <span className="text-emerald-400">{lastRetryResult.retries_succeeded}</span> | 
                    Retries Failed: <span className="text-amber-400">{lastRetryResult.retries_failed}</span> | 
                    Enforcements: <span className="text-red-400">{lastRetryResult.enforcements}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* ================================================================ */}
      {/* FULL LEDGER TABLE (when tenant selected and has more than 10) */}
      {/* ================================================================ */}
      {selectedTenantId && ledgerTotal > 10 && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg text-zinc-100">Full Billing Ledger</CardTitle>
            <CardDescription className="text-zinc-400">
              {ledgerTotal} entries • Page {ledgerPage} of {Math.ceil(ledgerTotal / 10) || 1}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {ledgerLoading ? (
              <div className="space-y-2">
                {[1,2,3].map(i => <Skeleton key={i} className="h-12 bg-zinc-800" />)}
              </div>
            ) : ledger.length > 0 ? (
              <>
                <div className="border border-zinc-700 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-zinc-800 hover:bg-zinc-800">
                        <TableHead className="text-zinc-400">Date</TableHead>
                        <TableHead className="text-zinc-400">Type</TableHead>
                        <TableHead className="text-zinc-400">Reason</TableHead>
                        <TableHead className="text-zinc-400">Amount</TableHead>
                        <TableHead className="text-zinc-400">Status</TableHead>
                        <TableHead className="text-zinc-400"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ledger.map((entry) => (
                        <TableRow key={entry.ledger_id} className="hover:bg-zinc-800">
                          <TableCell className="text-zinc-400 text-sm">
                            {formatTs(entry.created_at)}
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${LEDGER_TYPE_COLORS[entry.type] || 'bg-zinc-700'}`}>
                              {entry.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-zinc-400 text-sm">{entry.reason || '-'}</TableCell>
                          <TableCell className={`font-medium ${entry.type === 'REFUND' ? 'text-purple-400' : 'text-zinc-200'}`}>
                            {entry.type === 'REFUND' ? '-' : ''}{formatCurrency(entry.amount_cents)}
                          </TableCell>
                          <TableCell>
                            {entry.status === 'SUCCEEDED' ? (
                              <CheckCircle size={16} className="text-emerald-400" />
                            ) : entry.status === 'FAILED' ? (
                              <XCircle size={16} className="text-red-400" />
                            ) : (
                              <Clock size={16} className="text-amber-400" />
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => loadLedgerDetail(entry.ledger_id)}
                                className="text-zinc-400 hover:text-zinc-200"
                              >
                                Details
                              </Button>
                              {entry.type === 'CHARGE' && entry.status === 'SUCCEEDED' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openRefundModal(entry)}
                                  className="text-purple-400 hover:text-purple-300"
                                  data-testid={`refund-btn-${entry.ledger_id}`}
                                >
                                  Refund
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-zinc-500">
                    Showing {(ledgerPage - 1) * 10 + 1} - {Math.min(ledgerPage * 10, ledgerTotal)} of {ledgerTotal}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadLedger(selectedTenantId, ledgerPage - 1)}
                      disabled={ledgerPage <= 1}
                      className="bg-zinc-800 border-zinc-600 text-zinc-200"
                    >
                      <CaretLeft size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadLedger(selectedTenantId, ledgerPage + 1)}
                      disabled={ledgerPage >= Math.ceil(ledgerTotal / 10)}
                      className="bg-zinc-800 border-zinc-600 text-zinc-200"
                    >
                      <CaretRight size={16} />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                No ledger entries found for this tenant
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ledger Entry Detail Modal */}
      <Dialog open={!!selectedLedgerEntry} onOpenChange={() => setSelectedLedgerEntry(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Ledger Entry Details</DialogTitle>
          </DialogHeader>
          {selectedLedgerEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-zinc-500">Ledger ID</div>
                  <div className="text-zinc-200 font-mono text-xs break-all">{selectedLedgerEntry.ledger_id}</div>
                </div>
                <div>
                  <div className="text-zinc-500">Type</div>
                  <Badge className={`${LEDGER_TYPE_COLORS[selectedLedgerEntry.type]}`}>
                    {selectedLedgerEntry.type}
                  </Badge>
                </div>
                <div>
                  <div className="text-zinc-500">Amount</div>
                  <div className="text-zinc-200 font-medium">{formatCurrency(selectedLedgerEntry.amount_cents)}</div>
                </div>
                <div>
                  <div className="text-zinc-500">Status</div>
                  <div className="text-zinc-200">{selectedLedgerEntry.status}</div>
                </div>
                <div>
                  <div className="text-zinc-500">Reason</div>
                  <div className="text-zinc-200">{selectedLedgerEntry.reason || '-'}</div>
                </div>
                <div>
                  <div className="text-zinc-500">Scope</div>
                  <div className="text-zinc-200">{selectedLedgerEntry.scope}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-zinc-500">Created At</div>
                  <div className="text-zinc-200">{formatTs(selectedLedgerEntry.created_at)}</div>
                </div>
                {selectedLedgerEntry.description && (
                  <div className="col-span-2">
                    <div className="text-zinc-500">Description</div>
                    <div className="text-zinc-200">{selectedLedgerEntry.description}</div>
                  </div>
                )}
                {selectedLedgerEntry.error_code && (
                  <div className="col-span-2">
                    <div className="text-zinc-500">Error</div>
                    <div className="text-red-400">{selectedLedgerEntry.error_code}</div>
                  </div>
                )}
                {selectedLedgerEntry.parent_ledger_id && (
                  <div className="col-span-2">
                    <div className="text-zinc-500">Parent Ledger ID</div>
                    <div className="text-zinc-200 font-mono text-xs">{selectedLedgerEntry.parent_ledger_id}</div>
                  </div>
                )}
                {selectedLedgerEntry.feature_ids?.length > 0 && (
                  <div className="col-span-2">
                    <div className="text-zinc-500">Feature IDs</div>
                    <div className="text-zinc-200 text-xs">{selectedLedgerEntry.feature_ids.join(', ')}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedLedgerEntry(null)} className="bg-zinc-800 border-zinc-600 text-zinc-200">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={refundModalOpen} onOpenChange={setRefundModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Process Refund</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Refund for ledger entry {refundEntry?.ledger_id?.slice(0, 12)}...
            </DialogDescription>
          </DialogHeader>
          {refundEntry && (
            <div className="space-y-4">
              <div className="p-3 bg-zinc-800 rounded-lg text-sm">
                <div className="text-zinc-400">Original Charge</div>
                <div className="text-zinc-200 text-lg font-medium">{formatCurrency(refundEntry.amount_cents)}</div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Refund Amount ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={(refundEntry.amount_cents / 100).toFixed(2)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="bg-zinc-800 border-zinc-600 text-zinc-100"
                  placeholder="Leave empty for full refund"
                  data-testid="refund-amount-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Max: ${(refundEntry.amount_cents / 100).toFixed(2)}
                </p>
              </div>
              
              <div className="p-3 bg-amber-900/20 border border-amber-600/30 rounded-lg">
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <Warning size={16} />
                  This will issue a real refund via Finix. This action cannot be undone.
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRefundModalOpen(false)} className="bg-zinc-800 border-zinc-600 text-zinc-200">
              Cancel
            </Button>
            <Button
              onClick={handleRefund}
              disabled={processingRefund}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="confirm-refund-btn"
            >
              {processingRefund ? (
                <ArrowClockwise size={16} className="mr-2 animate-spin" />
              ) : (
                <ArrowCounterClockwise size={16} className="mr-2" />
              )}
              Process Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1: Disable Tenant Access Modal */}
      <Dialog open={disableModalOpen} onOpenChange={setDisableModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-red-400">Disable Tenant Access</DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will immediately block all users of this tenant from accessing the application.
              Platform admins will still be able to inspect the tenant.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-900/20 border border-red-600/30 rounded-lg">
              <div className="text-red-400 text-sm font-medium">Warning</div>
              <div className="text-zinc-400 text-sm">
                Tenant: <span className="text-zinc-200">{selectedTenantId}</span>
              </div>
              <div className="text-zinc-400 text-sm">
                Company: <span className="text-zinc-200">{overview?.company_name || 'Unknown'}</span>
              </div>
            </div>
            
            <div>
              <label className="block text-zinc-400 text-sm mb-1">
                Reason for disabling (required)
              </label>
              <Input
                placeholder="Enter reason..."
                value={disableReason}
                onChange={(e) => setDisableReason(e.target.value)}
                className="bg-zinc-800 border-zinc-600 text-zinc-200"
                data-testid="disable-reason-input"
              />
            </div>
            
            <div>
              <label className="block text-zinc-400 text-sm mb-1">
                Type <span className="text-red-400 font-mono">DISABLE_TENANT</span> to confirm
              </label>
              <Input
                placeholder="DISABLE_TENANT"
                value={disableConfirm}
                onChange={(e) => setDisableConfirm(e.target.value)}
                className="bg-zinc-800 border-zinc-600 text-zinc-200 font-mono"
                data-testid="disable-confirm-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setDisableModalOpen(false);
                setDisableReason('');
                setDisableConfirm('');
              }}
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisableTenant}
              disabled={processingAccess || disableConfirm !== 'DISABLE_TENANT' || disableReason.length < 5}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="disable-submit-btn"
            >
              {processingAccess ? 'Disabling...' : 'Disable Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P1: Enable Tenant Access Modal */}
      <Dialog open={enableModalOpen} onOpenChange={setEnableModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-emerald-400">Re-enable Tenant Access</DialogTitle>
            <DialogDescription className="text-zinc-400">
              This will restore access for all users of this tenant.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-emerald-900/20 border border-emerald-600/30 rounded-lg">
              <div className="text-zinc-400 text-sm">
                Tenant: <span className="text-zinc-200">{selectedTenantId}</span>
              </div>
              <div className="text-zinc-400 text-sm">
                Company: <span className="text-zinc-200">{overview?.company_name || 'Unknown'}</span>
              </div>
            </div>
            
            <div>
              <label className="block text-zinc-400 text-sm mb-1">
                Reason for re-enabling (optional)
              </label>
              <Input
                placeholder="Enter reason..."
                value={enableReason}
                onChange={(e) => setEnableReason(e.target.value)}
                className="bg-zinc-800 border-zinc-600 text-zinc-200"
                data-testid="enable-reason-input"
              />
            </div>
            
            <div>
              <label className="block text-zinc-400 text-sm mb-1">
                Type <span className="text-emerald-400 font-mono">ENABLE_TENANT</span> to confirm
              </label>
              <Input
                placeholder="ENABLE_TENANT"
                value={enableConfirm}
                onChange={(e) => setEnableConfirm(e.target.value)}
                className="bg-zinc-800 border-zinc-600 text-zinc-200 font-mono"
                data-testid="enable-confirm-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setEnableModalOpen(false);
                setEnableReason('');
                setEnableConfirm('');
              }}
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEnableTenant}
              disabled={processingAccess || enableConfirm !== 'ENABLE_TENANT'}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="enable-submit-btn"
            >
              {processingAccess ? 'Enabling...' : 'Enable Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
